import styled from '@emotion/styled';
import {
    Carousel,
    getPageUrl,
    useAnalyticsClickEvent,
    useAnalyticsViewEvent,
} from 'rwl-ui';
import { JOB_DETAILS_HEIGHT } from './JobCardDetails';
import { FeaturedJob } from 'rwl-shared/dist/models';
import {
    DetailsMode,
    useBreakpoint,
    useContentDetails,
    useJobListContext,
} from '../../../providers';
import { When } from 'react-if';
import { IMAGE_HEIGHT, JobCard } from './JobCard';
import { useWidgetContainerIntersection } from '../../../hooks/intersectionObservers/useWidgetContainerIntersection';
import { ClickTargets, ViewTargets } from 'rwl-shared/dist/models/analytics';
import { ForwardedRef, forwardRef, useCallback, useEffect } from 'react';
import { useJob } from '../../../fetchers';

const JobCardsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(224px, 1fr));
    grid-gap: var(--rwl-spacing-lg); /* Space between rows and columns */
    align-items: start; /* Align content to the top of each row */
    margin-bottom: var(--rwl-spacing-md);
`;

const JobCarouselContainer = styled.div`
    display: flex;
`;

export const JobCards = forwardRef(
    (_, ref: ForwardedRef<HTMLDivElement>): JSX.Element => {
        const { jobsList: jobs } = useJobListContext();

        const { job } = useJob();
        const sendViewEvent = useAnalyticsViewEvent();
        useEffect(() => {
            if (job) {
                sendViewEvent({
                    url: job.id,
                    target: ViewTargets.JOB_DETAILS,
                    workSite: job.id,
                });
            }
        }, [job]);

        return (
            <>
                <LargeViewJobCards jobs={jobs} ref={ref} />
                <MobileViewJobCards jobs={jobs} ref={ref} />
            </>
        );
    }
);

const LargeViewJobCards = forwardRef(
    (
        { jobs }: { jobs: FeaturedJob[] },
        ref: ForwardedRef<HTMLDivElement>
    ): JSX.Element => {
        const { isAboveBreakpoint, isWithin } = useBreakpoint();

        return (
            <When condition={isAboveBreakpoint('sm')}>
                <JobCardsContainer>
                    {jobs.map((job) => (
                        <LargeViewJobCardWithEvents
                            key={`${job.id}_job_card`}
                            job={job}
                            width={isWithin('xs', 'sm') ? 'auto' : '100%'}
                            ref={ref}
                        />
                    ))}
                </JobCardsContainer>
            </When>
        );
    }
);

const LargeViewJobCardWithEvents = forwardRef(
    (
        { job, width }: { job: FeaturedJob; width: string },
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        const { jobsList: jobs } = useJobListContext();
        const { openDetails } = useContentDetails();
        const sendViewEvent = useAnalyticsViewEvent();
        const sendClickEvent = useAnalyticsClickEvent();
        const { targetRef } = useWidgetContainerIntersection(
            () => {
                sendViewEvent({
                    url: getPageUrl(),
                    target: ViewTargets.JOB_SUMMARY,
                    workSite: job.id,
                });
            },
            () => void 0,
            { silenceMissingProviderError: true, threshold: 0.5 }
        );

        const setRefs = useCallback(
            (node: HTMLDivElement | null) => {
                if (ref) {
                    if (typeof ref === 'function') {
                        ref(node);
                    } else {
                        (
                            ref as React.MutableRefObject<HTMLDivElement | null>
                        ).current = node;
                    }
                }
                if (targetRef) {
                    if (typeof targetRef === 'function') {
                        targetRef(node);
                    } else {
                        (
                            targetRef as React.MutableRefObject<HTMLDivElement | null>
                        ).current = node;
                    }
                }
            },
            [ref, targetRef]
        );

        const handleOnClick = useCallback(() => {
            const selectedJob = jobs.find((j) => j.id === job.id);
            openDetails(
                selectedJob?.id,
                selectedJob?.reviewId,
                jobs,
                DetailsMode.JOB
            );
            sendClickEvent({
                url: getPageUrl(),
                target: ClickTargets.JOB,
                workSite: job.id,
            });
        }, []);

        return (
            <JobCard
                ref={setRefs}
                job={job}
                cardStyle={{
                    height: '180px',
                    width,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: 'var(--rwl-radius-sm)',
                }}
                onClick={handleOnClick}
            />
        );
    }
);

const MobileViewJobCards = forwardRef(
    ({ jobs }: { jobs: FeaturedJob[] }, ref: ForwardedRef<HTMLDivElement>) => {
        const { isBelowBreakpoint, isWithin } = useBreakpoint();
        const sendViewEvent = useAnalyticsViewEvent();
        const sendClickEvent = useAnalyticsClickEvent();

        const handleOnSelectChange = useCallback(
            (selectedIndex: number, numberInView: number) => {
                if (jobs.length === 0) {
                    return;
                }

                const jobsInView = jobs.slice(
                    selectedIndex,
                    selectedIndex + numberInView
                );
                jobsInView.forEach((job) => {
                    sendViewEvent({
                        url: getPageUrl(),
                        target: ViewTargets.JOB_SUMMARY,
                        content: job.id,
                    });
                });
            },
            [jobs]
        );

        const handleOnNextClick = () => {
            sendClickEvent({
                url: getPageUrl(),
                target: ClickTargets.NEXT,
            });
        };

        const handleOnPrevClick = () => {
            sendClickEvent({
                url: getPageUrl(),
                target: ClickTargets.PREV,
            });
        };

        return (
            <When condition={isBelowBreakpoint('sm')}>
                <JobCarouselContainer>
                    <Carousel
                        height={IMAGE_HEIGHT + JOB_DETAILS_HEIGHT + 32}
                        render={(job: FeaturedJob) => (
                            <MobileViewJobCardWithEvents
                                key={`${job.id}_mobile_job_card`}
                                containerStyle={
                                    isWithin('xs', 'sm')
                                        ? {
                                              width: '250px',
                                          }
                                        : {}
                                }
                                job={job}
                                ref={ref}
                            />
                        )}
                        content={jobs}
                        onSelectChange={handleOnSelectChange}
                        onNextClick={handleOnNextClick}
                        onPrevClick={handleOnPrevClick}
                    />
                </JobCarouselContainer>
            </When>
        );
    }
);

const MobileViewJobCardWithEvents = forwardRef(
    (
        {
            containerStyle,
            job,
        }: {
            containerStyle: { width?: string };
            job: FeaturedJob;
        },
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        const { jobsList: jobs } = useJobListContext();
        const sendClickEvent = useAnalyticsClickEvent();
        const { openDetails } = useContentDetails();
        const handleOnCardClick = (id: string) => {
            const selectedJob = jobs.find((j) => j.id === id);
            openDetails(
                selectedJob?.id,
                selectedJob?.reviewId,
                jobs,
                DetailsMode.JOB
            );
            sendClickEvent({
                url: getPageUrl(),
                target: ClickTargets.JOB,
                content: id,
            });
        };

        return (
            <JobCard
                disabledCarousel
                job={job}
                containerStyle={containerStyle}
                cardStyle={{
                    height: IMAGE_HEIGHT,
                    width: '100%',
                    borderBottomLeftRadius: 0,
                    borderTopLeftRadius: 'var(--rwl-radius-sm)',
                    borderTopRightRadius: 'var(--rwl-radius-sm)',
                }}
                onClick={() => handleOnCardClick(job.id)}
                ref={ref}
            />
        );
    }
);
